import { FC, ReactElement, useCallback, useState } from 'react';
import { Checkbox, CheckboxSize, CheckboxType } from '@nx-frontend/shared/ui/checkbox';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { upsertWorkspaceNote } from '@nx-frontend/shared/api/webnotes';
import { MenuItemDefault, NoteDto, PopupType, TranslatesFnType } from '@nx-frontend/shared/types';
import { OpenModalParams } from '@nx-frontend/shared/types';
import { ContextMenuItemData, ContextMenuItemType } from '@nx-frontend/shared/types';

interface GetPageMenuItemsParams {
  note: NoteDto;
  slug: string[];
  routerPush: (url: string) => void;
  onSearch: () => void;
  onUndo: () => void;
  onRedo: () => void;
  updateNote: (noteFields: Partial<NoteDto>) => void;
  openModal: (modalState: OpenModalParams) => void;
  closeModal: () => void;
  onDownload: (format: string, size?: string) => void;
  onDelete: () => void;
}

interface SwitchMenuItemProps {
  name: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  icon: ReactElement;
}

interface GetDownloadSubMenuItemsParams {
  onDownload: (format: string, size?: string) => void;
}

const SwitchMenuItemComponent: FC<SwitchMenuItemProps> = ({ name, icon, checked, onChange }) => {
  const [isChecked, setIsChecked] = useState(checked);
  const handleChange = useCallback(
    (value: boolean) => {
      onChange(value);
      setIsChecked(!isChecked);
    },
    [onChange, isChecked]
  );
  return (
    <div className="flex flex-row w-full justify-between items-center pb-1">
      <div className="flex gap-2 items-center w-full">
        <Icon icon={icon} className="w-4 h-4 text-neutral-500" />
        <span className="text-sm">{name}</span>
        <span className="self-end ml-auto">
          <Checkbox
            id="index"
            checked={isChecked}
            type={CheckboxType.SWITCH}
            boxSize={CheckboxSize.SM}
            onChange={handleChange}
          />
        </span>
      </div>
    </div>
  );
};

/* Page menu data */
const searchMenuItem: MenuItemDefault = Object.freeze({
  id: 'search',
  title: 'Find in page',
  icon: IconsMap.SEARCH,
  type: ContextMenuItemType.DEFAULT,
});

const downloadMenuItem: MenuItemDefault = Object.freeze({
  id: 'download',
  title: 'Download',
  icon: IconsMap.DOWNLOAD,
  type: ContextMenuItemType.DEFAULT,
  subMenuAdditionStyles: 'min-w-[124px]',
  isSeparator: true,
});

const getDownloadSubMenuItems = (prams: GetDownloadSubMenuItemsParams): ContextMenuItemData[] => {
  return [
    {
      title: 'HTML',
      icon: IconsMap.HTML,
      iconStyles: 'hidden',
      type: ContextMenuItemType.DEFAULT,
      action: () => {
        prams.onDownload('html');
      },
    },
    {
      title: 'PDF',
      icon: IconsMap.PDF,
      iconStyles: 'hidden',
      type: ContextMenuItemType.DEFAULT,
      subMenuAdditionStyles: 'min-w-[124px]',
      subMenu: ['A1', 'A2', 'A3', 'A4', 'A5'].map((size) => ({
        title: size,
        icon: IconsMap.PDF,
        iconStyles: 'hidden',
        type: ContextMenuItemType.DEFAULT,
        action: () => {
          prams.onDownload('pdf', size);
        },
      })),
    },
  ];
};

const readOnlyMenuItem: ContextMenuItemData = Object.freeze({
  id: 'read-only',
  type: ContextMenuItemType.HTML,
  closeOnClick: false,
  html: <span></span>,
});

// const pageNotificationsMenuItem: ContextMenuItemData = Object.freeze({
//   id: 'page-notifications',
//   type: ContextMenuItemType.HTML,
//   html: (
//     <div className="flex flex-row w-full justify-between items-center pb-1">
//       <div className="flex gap-2 items-center w-full">
//         <Icon icon={IconsMap.BELL} className="w-4 h-4 text-neutral-500" />
//         <span className="text-sm">Page notifications</span>
//         <span className="self-end ml-auto">
//           <Checkbox
//             id="index"
//             checked={true}
//             type={CheckboxType.SWITCH}
//             boxSize={CheckboxSize.SM}
//             onChange={() => {
//               return false;
//             }}
//           />
//         </span>
//       </div>
//     </div>
//   ),
// });

const fullWidthMenuItem: ContextMenuItemData = Object.freeze({
  id: 'fullWidth',
  type: ContextMenuItemType.HTML,
  closeOnClick: false,
  html: <span></span>,
});

// const showResolvedCommentMenuItem: ContextMenuItemData = Object.freeze({
//   id: 'show-resolved-comments',
//   type: ContextMenuItemType.HTML,
//   html: (
//     <div className="flex flex-row w-full justify-between items-center pb-1">
//       <div className="flex gap-2 items-center w-full">
//         <Icon icon={IconsMap.CIRCLE_COMMENT} className="w-4 h-4 text-neutral-500" />
//         <span className="text-sm">Show resolved comments</span>
//         <span className="self-end ml-auto">
//           <Checkbox
//             id="index"
//             checked={true}
//             type={CheckboxType.SWITCH}
//             boxSize={CheckboxSize.SM}
//             onChange={() => {
//               return false;
//             }}
//           />
//         </span>
//       </div>
//     </div>
//   ),
//   isSeparator: true,
// });

const undoMenuItem: MenuItemDefault = Object.freeze({
  id: 'undo',
  title: 'Undo',
  icon: IconsMap.ARROW_BACKWARD,
  type: ContextMenuItemType.DEFAULT,
});

const redoMenuItem: MenuItemDefault = Object.freeze({
  id: 'redo',
  title: 'Redo',
  icon: IconsMap.ARROW_FORWARD,
  type: ContextMenuItemType.DEFAULT,
});

const deleteMenuItem: MenuItemDefault = Object.freeze({
  id: 'delete',
  title: 'Delete',
  icon: IconsMap.TRASH,
  type: ContextMenuItemType.DEFAULT,
});

export function getPageMenuItems(
  {
    note,
    slug,
    routerPush,
    onSearch,
    onUndo,
    onRedo,
    updateNote,
    openModal,
    closeModal,
    onDownload,
    onDelete,
  }: GetPageMenuItemsParams,
  t: TranslatesFnType
): ContextMenuItemData[] | undefined {
  const extendedSearchMenuItem = Object.assign({}, searchMenuItem, {
    title: t('webnotes.webnotes-find_in_page') ?? searchMenuItem.title,
    action: () => {
      onSearch();
    },
  });

  const extendsDownloadMenuItem = Object.assign({}, downloadMenuItem, {
    title: t('webnotes.webnotes-download') ?? downloadMenuItem.title,
    subMenu: getDownloadSubMenuItems({ onDownload }),
  });

  const extendedReadOnlyMenuItem = Object.assign({}, readOnlyMenuItem, {
    html: (
      <SwitchMenuItemComponent
        {...{
          name: t('webnotes.webnotes-read_only'),
          icon: IconsMap.EYE_OPEN,
          checked: !note.editnote,
          onChange: async (value) => {
            const updatedNote = await upsertWorkspaceNote({
              workspaceId: note.workspaceId,
              noteId: note.globalId,
              note: {
                editnote: !value,
              },
            });
            if (updatedNote) {
              updateNote(updatedNote);
            }
          },
        }}
      />
    ),
  });
  const extendedFullWidthMenuItem = Object.assign({}, fullWidthMenuItem, {
    html: (
      <SwitchMenuItemComponent
        {...{
          name: t('webnotes.webnotes-full_width'),
          icon: IconsMap.FULLSCREEN_IN,
          checked: note.isFullwidth,
          onChange: async (value) => {
            const updatedNote = await upsertWorkspaceNote({
              workspaceId: note.workspaceId,
              noteId: note.globalId,
              note: {
                isFullwidth: value,
              },
            });
            if (updatedNote) {
              updateNote(updatedNote);
            }
          },
        }}
      />
    ),
  });
  const extendedUndoMenuItem = Object.assign({}, undoMenuItem, {
    title: t('webnotes.webnotes-undo') ?? undoMenuItem.title,
    action: () => {
      onUndo();
    },
  });
  const extendedRedoMenuItem = Object.assign({}, redoMenuItem, {
    title: t('webnotes.webnotes-redo') ?? redoMenuItem.title,
    action: () => {
      onRedo();
    },
  });
  const extendedDeleteMenuItem = Object.assign({}, deleteMenuItem, {
    title: t('webnotes.webnotes-delete') ?? deleteMenuItem.title,
    action: async () => {
      openModal({
        title: t('webnotes.webnotes-page_removal'),
        content: PopupType.DIALOG,
        showCloseButton: true,
        closeButtonSize: 18,
        payload: {
          payloadType: PopupType.DIALOG,
          text: t('webnotes.webnotes-delete_item_description', { itemName: note.title }),
          buttons: [
            {
              text: t('webnotes.webnotes-cancel'),
            },
            {
              text: t('webnotes.webnotes-delete'),
              main: true,
              onClick: () => {
                const [, , globalId] = slug;
                if (globalId === note.globalId) {
                  routerPush('/space/' + note.workspaceId + '/folder/' + note.parentId);
                }
                upsertWorkspaceNote({
                  workspaceId: note.workspaceId,
                  noteId: note.globalId,
                  note: {
                    parentId: 'trash',
                    rootParentId: note.parentId,
                  },
                });
                closeModal();
                onDelete();
              },
            },
          ],
        },
      });
    },
  });
  return [
    extendedSearchMenuItem,
    extendsDownloadMenuItem,
    extendedReadOnlyMenuItem,
    // pageNotificationsMenuItem,
    extendedFullWidthMenuItem,
    // showResolvedCommentMenuItem,
    extendedUndoMenuItem,
    extendedRedoMenuItem,
    extendedDeleteMenuItem,
  ];
}
